/* eslint-disable no-extend-native */
import { getOriginByEnv } from 'core-lib/config/config';
import { isValid } from 'core-lib/utils/utils';
import dayjs from 'dayjs';
import {
  ModelDetailType,
  SeoDataType,
  StrapiContentType,
  VariantType,
} from 'src/modules/newCars/types';
import { formatPrice } from './helper';

String.prototype.replaceAll = function (target, payload) {
  if (!isValid(target)) {
    return target;
  }
  const regex = new RegExp(target, 'g');
  return this.valueOf().replace(regex, payload);
};

export function processListingSeoData(
  content,
  seoData: SeoDataType,
  brandName
) {
  if (!isValid(content)) {
    return null;
  }
  const res = content
    ?.replaceAll('<brand-name>', brandName)
    .replaceAll('<base-model-price>', formatPrice(seoData?.baseModelPrice))
    .replaceAll('<base-model-name>', seoData?.baseModel)
    .replaceAll('<top-model-price>', formatPrice(seoData?.topModelPrice))
    .replaceAll('<top-model-name>', seoData?.topModel)
    .replaceAll('<no-of-models>', seoData?.noOfModels || '-')
    .replaceAll('<no-of-hatchback>', seoData?.noOfHatchback || '-')
    .replaceAll('<no-of-sedan>', seoData?.noOfSedan || '-')
    .replaceAll('<no-of-suv>', seoData?.noOfSuv || '-')
    .replaceAll('<no-of-5-seater>', seoData?.noOf5Seater || '-')
    .replaceAll('<no-of-6-seater>', seoData?.noOf6Seater || '-')
    .replaceAll('<no-of-7-seater>', seoData?.noOf7Seater || '-')
    .replaceAll('<no-of-manual>', seoData?.noOfManual || '-')
    .replaceAll('<no-of-automatic>', seoData?.noOfAutomatic || '-')
    .replaceAll('<no-of-petrol>', seoData?.noOfPetrol || '-')
    .replaceAll('<no-of-diesel>', seoData?.noOfDiesel || '-')
    .replaceAll('<no-of-electric>', seoData?.noOfElectric || '-')
    .replaceAll('<no-of-cng>', seoData?.noOfCng || '-')
    .replaceAll('<no-of-hybrid>', seoData?.noOfHybrid || '-')
    .replaceAll('<seating-capacity>', '-')
    .replaceAll('<body-type>', '');
  return res;
}
export function madeColorNames(colorsArray) {
  let colorName = "";
  colorsArray?.map((color, index) => {
    colorName += color?.color;
    if (index < colorsArray?.length - 1) {
      colorName += ", "
    }
  })
  return colorName
}
export function fuelTypes(fuelArray) {
  let fuelNames = "";
  fuelArray?.map((fuel, index) => {
    fuelNames += fuel;
    if (index < fuelArray?.length - 1) {
      fuelNames += ", "
    }
  })
  return fuelNames
}
export function processModelSeoData(content: string, data: ModelDetailType, cityName?: string) {
  if (typeof content !== 'string') return null;
  const res = content
    ?.replaceAll('<brand-name>', data?.hero?.brandName || '-')
    .replaceAll('<model-name>', data?.hero?.modelName || '-')
    .replaceAll('<no-of-variants>', data?.variants?.length?.toString() || '-')
    .replaceAll('<base-variant-name>', data?.modelSeoData?.baseVariant || '-')
    .replaceAll(
      '<base-variant-price>',
      formatPrice(data?.modelSeoData?.baseVariantPrice)?.toString()
    )
    .replaceAll('<top-variant-name>', data?.modelSeoData?.topVariant || '-')
    .replaceAll(
      '<top-variant-price>',
      formatPrice(data?.modelSeoData?.topVariantPrice)?.toString()
    )
    .replaceAll(
      '<seating-capacity>',
      data?.specifications?.seatingCapacity || '-'
    )
    .replaceAll('<fuel-type>', fuelTypes(data?.hero?.fuelType) || '-')
    .replaceAll('<mileage>', data?.specifications?.mileage || '-')
    .replaceAll('<engine-type>', data?.specifications?.engine || '-')
    .replaceAll(
      '<no-of-colors>',
      data?.hero?.colors?.length.toString() || '-'
    )
    .replaceAll(
      '<base-model-mileage>',
      data?.modelSeoData?.baseVariantMileage || 'NA'
    )
    .replaceAll(
      '<top-model-mileage>',
      data?.modelSeoData?.topVariantMileage || 'NA'
    )
    .replaceAll('<body-type>', data?.specifications?.bodyType || '-')
    .replaceAll('<current-year>', `${dayjs().year()}` || '-')
    .replaceAll('<current-month>', `${dayjs().format('MMMM')}` || '-')
    .replaceAll('<image-count>', (data?.vehicleImages?.exterior?.length + data?.vehicleImages?.interior?.length).toString() || '-')
    .replaceAll("<all-color-name>", (data?.hero?.colors?.length > 0 ? madeColorNames(data?.hero?.colors): "white etc") || "-")
    .replaceAll("<place>", cityName || "NA")
  return res;
}

export function processSeoModelDetailData(
  strapiContent: StrapiContentType,
  data: ModelDetailType,
  seoData?: any,
  endSlug?: string,
  cityName?: string,
) {
  const seoTitle = processModelSeoData(
    seoData?.seoTitle || strapiContent?.seoTitle,
    data,
    cityName
  );

  let seoCanonicalUrl = strapiContent?.seoCanonicalUrl
    ?.replaceAll('<brand-name>', data?.modelSeoData?.brandSlug)
    ?.replaceAll('<model-name>', data?.modelSeoData?.modelSlug);
  if (endSlug) {
    seoCanonicalUrl += `/${endSlug}`;
  }
  const seoDescription = processModelSeoData(
    seoData?.seoDescription || strapiContent?.seoDescription,
    data,
    cityName
  );
  let schema = '';
  if (strapiContent?.schema) {
    schema = JSON.parse(
      processModelSeoData(JSON.stringify(strapiContent?.schema), data, cityName)
    );
  }
  return {
    ...strapiContent,
    seoTitle,
    seoCanonicalUrl,
    seoDescription,
    schema,
  };
}

export function processVariantContent(content, data: VariantType) {
  if (typeof content !== 'string') {
    return content;
  }
  const res = content
    ?.replaceAll('<brand-name>', data?.brandName || '-')
    .replaceAll('<model-name>', data?.modelName || '-')
    .replaceAll('<variant-name>', data?.variantName || '-')
    .replaceAll('<engine-type>', data?.specifications?.engine || '-')
    .replaceAll('<variant-price>', data?.price)
    .replaceAll(
      '<seating-capacity>',
      data?.seatingCapacity || '-'
    )
    .replaceAll(
      '<max-torque>',
      data?.maxTorqueSlug || '-'
    )
    .replaceAll(
      '<max-torque-rpm>',
      data?.detailedSpecifications?.engineAndTransmission?.max_torque_rpm || '-'
    )
    .replaceAll(
      '<max-power>',
      data?.maxPowerSlug || '-'
    )
    .replaceAll(
      '<fuel-tank-capacity>',
      data?.detailedSpecifications?.engineAndTransmission?.fuel_tank_capacity ||
      '-'
    )
    .replaceAll(
      '<airbags>',
      data?.detailedSpecifications?.engineAndTransmission?.airbags || '-'
    )
    .replaceAll('<mileage>', data?.mileageSlug || '-')
    .replaceAll('<transmission>', data?.transmissionSlug);
  return res;
}

export function handleSeoData(strapiContent, data, brandName, pagePath) {
  let filterTitle = pagePath?.replaceAll('/', ' ').replaceAll('new-cars', '');
  filterTitle = filterTitle?.replaceAll('-', ' ');

  const seoTitle = processListingSeoData(
    strapiContent?.seoTitle,
    data,
    brandName
  )?.replaceAll('<filter>', filterTitle);

  const seoDescription = processListingSeoData(
    strapiContent?.seoDescription,
    data,
    brandName
  )?.replaceAll('<filter>', filterTitle);
  let schema = '';
  if (typeof strapiContent?.schema === 'string') {
    schema = processListingSeoData(strapiContent?.schema, data, brandName);
  } else if (
    strapiContent?.schema &&
    typeof strapiContent?.schema === 'object'
  ) {
    schema = processListingSeoData(
      JSON.stringify(strapiContent?.schema),
      data,
      brandName
    );
  }
  if (schema) {
    schema = JSON.parse(schema);
  }
  let canonical = strapiContent?.seoCanonicalUrl?.replace(
    '<brand-name>',
    brandName.replace(/ /g, '-').toLowerCase()
  );
  if (strapiContent?.seoCanonicalUrl?.includes('<filter>')) {
    canonical = `${getOriginByEnv()}${pagePath}`;
  }
  return {
    ...strapiContent,
    seoTitle,
    seoCanonicalUrl: canonical,
    seoDescription,
    schema,
  };
}

export function handleVariantSeoData(
  strapiContent: StrapiContentType,
  data: VariantType
) {
  const seoTitle = processVariantContent(strapiContent?.seoTitle, data);

  let seoCanonicalUrl = strapiContent?.seoCanonicalUrl
    ?.replace(
      '<brand-name>',
      data?.brandName?.toLowerCase().replaceAll(' ', '-')
    )
    .replace(
      '<model-name>',
      data?.modelName?.toLowerCase().replaceAll(' ', '-')
    )
    .replace(
      '<variant-name>',
      data?.variantName.toLowerCase().replaceAll(' ', '-')
    );

  seoCanonicalUrl = seoCanonicalUrl?.replace(/[()]/g, '');

  const seoDescription = processVariantContent(
    strapiContent?.seoDescription,
    data
  );
  let schema = '';
  if (strapiContent?.schema) {
    schema = JSON.parse(
      processVariantContent(JSON.stringify(strapiContent?.schema), data)
    );
  }
  return { seoTitle, seoCanonicalUrl, seoDescription, schema };
}
